<template>
  <div class="radio-container">
    <!-- Skeleton Loader -->
    <div v-if="loading" class="skeleton-container">
      <div class="skeleton-logo"></div>
      <div class="skeleton-stars">
        <div v-for="i in 7" :key="i" class="skeleton-star"></div>
      </div>
      <div class="skeleton-play-btn"></div>
      <div class="skeleton-metadata">
        <div class="skeleton-line title-line"></div>
        <div class="skeleton-line"></div>
        <div class="skeleton-line short"></div>
      </div>
    </div>

    <!-- Pantalla de bloqueo para usuarios no premium -->
    <div v-else-if="!isPremium" class="premium-blocked">
      <div class="premium-icon">🔒</div>
      <h2>Contenido Premium</h2>
      <p>Debes tener una suscripción activa para acceder a JLHVISION Radio</p>
      <button class="subscribe-btn" @click="goToSubscription">Suscribirme</button>
    </div>

    <!-- Contenido de la radio (solo premium) -->
    <template v-else>
      <!-- Logo centrado -->
      <img src="/logo-radio.png" alt="Radio Logo" class="radio-logo">

      <!-- 7 Estrellas animadas -->
      <div class="stars-container">
        <i 
          v-for="star in 7" 
          :key="star"
          class="material-icons star"
          :class="{ 'star-active': isPlaying }"
        >star</i>
      </div>

      <!-- Botón Play/Pause -->
      <button class="play-btn" @click="togglePlay">
        <i class="material-icons">{{ isPlaying ? 'pause' : 'play_arrow' }}</i>
      </button>

      <!-- Metadatos del stream -->
      <div class="metadata" v-if="metadata">
        <div class="metadata-title">{{ metadata.title || 'Radio Online' }}</div>
        <div class="metadata-artist">{{ metadata.artist || 'En vivo' }}</div>
        <div class="metadata-bitrate">{{ metadata.bitrate || '128 kbps' }}</div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '../../services/api'
import { useAuthStore } from '../../stores/auth'

const router = useRouter()
const authStore = useAuthStore()

// Estado
const isPlaying = ref(false)
const metadata = ref(null)
const streamUrl = ref('')
const loading = ref(true)
const isPremium = ref(false)
let audio = null
let metadataInterval = null

// Verificar si el usuario es premium
const checkPremiumStatus = () => {
  const user = authStore.user
  if (user && user.is_premium === true) {
    isPremium.value = true
    return true
  } else {
    isPremium.value = false
    return false
  }
}

// Cargar configuración para obtener la URL del streaming
const loadSettings = async () => {
  try {
    const response = await api.get('/settings')
    streamUrl.value = response.data.setting.url_streaming_radio || ''
    console.log('URL del streaming:', streamUrl.value)
    // Inicializar audio después de cargar la URL
    initAudio()
  } catch (error) {
    console.error('Error cargando configuración:', error)
    streamUrl.value = 'https://live.hunter.fm/sertanejo_stream?ag=mp3'
    initAudio()
  }
}

// Inicializar audio
const initAudio = () => {
  if (!streamUrl.value) {
    console.error('No hay URL de streaming')
    return
  }
  
  audio = new Audio(streamUrl.value)
  audio.addEventListener('play', () => {
    isPlaying.value = true
    startMetadataPolling()
  })
  audio.addEventListener('pause', () => {
    isPlaying.value = false
    stopMetadataPolling()
  })
  audio.addEventListener('error', (e) => {
    console.error('Error de audio:', e)
    isPlaying.value = false
    stopMetadataPolling()
  })
}

// Toggle Play/Pause
const togglePlay = () => {
  if (!audio) {
    console.error('Audio no inicializado')
    return
  }
  
  if (isPlaying.value) {
    audio.pause()
    stopMetadataPolling()
  } else {
    audio.play().catch(error => {
      console.error('Error al reproducir:', error)
    })
    startMetadataPolling()
  }
}

// Obtener metadatos del stream
const fetchMetadata = async () => {
  metadata.value = {
    title: 'JLHVISION RADIO',
    artist: 'En vivo',
    bitrate: '128 kbps'
  }
}

const startMetadataPolling = () => {
  if (metadataInterval) clearInterval(metadataInterval)
  fetchMetadata()
  metadataInterval = setInterval(fetchMetadata, 10000)
}

const stopMetadataPolling = () => {
  if (metadataInterval) {
    clearInterval(metadataInterval)
    metadataInterval = null
  }
}

const goToSubscription = () => {
  router.push('/misuscripcion')
}

onMounted(async () => {
  // Evitar scroll en el body
  document.body.style.overflow = 'hidden'
  
  // Mostrar skeleton loader por al menos 500ms para que se vea
  await new Promise(resolve => setTimeout(resolve, 500))
  
  // Verificar premium
  const userIsPremium = checkPremiumStatus()
  
  // Si es premium, cargar la radio
  if (userIsPremium) {
    await loadSettings()
  }
  
  // Ocultar skeleton loader
  loading.value = false
})

onUnmounted(() => {
  // Restaurar scroll
  document.body.style.overflow = ''
  
  if (audio) {
    audio.pause()
    audio = null
  }
  stopMetadataPolling()
})
</script>

<style scoped>
/* 🔥 Evitar scroll en el contenedor */
.radio-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  background: #001b4a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: hidden;
}

/* ========== SKELETON LOADER ========== */
.skeleton-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.skeleton-logo {
  width: 180px;
  height: 80px;
  background: linear-gradient(90deg, #2a2a4a 25%, #3a3a5a 50%, #2a2a4a 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
  margin-bottom: 40px;
}

.skeleton-stars {
  display: flex;
  gap: 15px;
  margin-bottom: 40px;
}

.skeleton-star {
  width: 40px;
  height: 40px;
  background: linear-gradient(90deg, #2a2a4a 25%, #3a3a5a 50%, #2a2a4a 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 50%;
}

.skeleton-play-btn {
  width: 80px;
  height: 80px;
  background: linear-gradient(90deg, #2a2a4a 25%, #3a3a5a 50%, #2a2a4a 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 50%;
  margin-bottom: 40px;
}

.skeleton-metadata {
  width: 280px;
  background: rgba(255,255,255,0.05);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-line {
  height: 16px;
  background: linear-gradient(90deg, #2a2a4a 25%, #3a3a5a 50%, #2a2a4a 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-line.title-line {
  height: 22px;
  width: 70%;
  margin: 0 auto;
}

.skeleton-line.short {
  width: 50%;
  margin: 0 auto;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.radio-logo {
  max-width: 200px;
  width: 90%;
  height: auto;
  margin-bottom: 40px;
  animation: fadeIn 0.5s ease;
}

/* Estrellas */
.stars-container {
  display: flex;
  gap: 15px;
  margin-bottom: 40px;
  flex-wrap: wrap;
  justify-content: center;
}

.star {
  font-size: 40px;
  color: #fff;
  opacity: 0.3;
  transition: all 0.3s ease;
}

/* Animación de las estrellas cuando suena la radio */
.star-active {
  animation: starLoader 1.5s ease-in-out infinite;
}

/* Animación escalonada para cada estrella */
.star-active:nth-child(1) { animation-delay: 0s; }
.star-active:nth-child(2) { animation-delay: 0.1s; }
.star-active:nth-child(3) { animation-delay: 0.2s; }
.star-active:nth-child(4) { animation-delay: 0.3s; }
.star-active:nth-child(5) { animation-delay: 0.4s; }
.star-active:nth-child(6) { animation-delay: 0.5s; }
.star-active:nth-child(7) { animation-delay: 0.6s; }

@keyframes starLoader {
  0% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
  100% {
    opacity: 0.3;
    transform: scale(1);
  }
}

/* Botón Play/Pause */
.play-btn {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  margin-bottom: 40px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}

.play-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(0,0,0,0.3);
}

.play-btn:active {
  transform: scale(0.95);
}

.play-btn i {
  font-size: 48px;
  color: #001b4a;
}

/* Metadatos */
.metadata {
  text-align: center;
  color: white;
  background: rgba(255,255,255,0.1);
  padding: 20px;
  border-radius: 12px;
  max-width: 90%;
  width: 400px;
  backdrop-filter: blur(10px);
}

.metadata-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #ffd700;
}

.metadata-artist {
  font-size: 16px;
  margin-bottom: 8px;
  opacity: 0.9;
}

.metadata-bitrate {
  font-size: 12px;
  opacity: 0.7;
}

/* ========== PREMIUM BLOCKED ========== */
.premium-blocked {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  padding: 40px;
}

.premium-icon {
  font-size: 80px;
  margin-bottom: 20px;
}

.premium-blocked h2 {
  font-size: 28px;
  margin-bottom: 15px;
  color: #ffd700;
}

.premium-blocked p {
  font-size: 16px;
  margin-bottom: 30px;
  opacity: 0.9;
  max-width: 350px;
}

.subscribe-btn {
  background: linear-gradient(135deg, #ffd700, #ff8c00);
  color: #001b4a;
  border: none;
  padding: 14px 32px;
  font-size: 16px;
  font-weight: bold;
  border-radius: 40px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.subscribe-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
}

.subscribe-btn:active {
  transform: scale(0.98);
}

/* Animación de entrada */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .skeleton-logo {
    width: 150px;
    height: 60px;
  }
  
  .skeleton-star {
    width: 30px;
    height: 30px;
  }
  
  .skeleton-play-btn {
    width: 70px;
    height: 70px;
  }
  
  .radio-logo {
    max-width: 150px;
    margin-bottom: 30px;
  }
  
  .star {
    font-size: 30px;
  }
  
  .stars-container {
    gap: 10px;
    margin-bottom: 30px;
  }
  
  .play-btn {
    width: 70px;
    height: 70px;
    margin-bottom: 30px;
  }
  
  .play-btn i {
    font-size: 40px;
  }
  
  .metadata-title {
    font-size: 18px;
  }
  
  .metadata-artist {
    font-size: 14px;
  }
  
  .premium-blocked h2 {
    font-size: 24px;
  }
  
  .premium-blocked p {
    font-size: 14px;
  }
  
  .subscribe-btn {
    padding: 12px 28px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .skeleton-logo {
    width: 120px;
    height: 50px;
  }
  
  .skeleton-star {
    width: 24px;
    height: 24px;
  }
  
  .skeleton-play-btn {
    width: 60px;
    height: 60px;
  }
  
  .skeleton-metadata {
    width: 240px;
  }
  
  .radio-logo {
    max-width: 120px;
    margin-bottom: 25px;
  }
  
  .star {
    font-size: 24px;
  }
  
  .stars-container {
    gap: 8px;
    margin-bottom: 25px;
  }
  
  .play-btn {
    width: 60px;
    height: 60px;
    margin-bottom: 25px;
  }
  
  .play-btn i {
    font-size: 32px;
  }
  
  .metadata {
    padding: 15px;
  }
  
  .metadata-title {
    font-size: 16px;
  }
  
  .metadata-artist {
    font-size: 13px;
  }
  
  .premium-blocked h2 {
    font-size: 22px;
  }
  
  .premium-blocked p {
    font-size: 13px;
  }
  
  .subscribe-btn {
    padding: 10px 24px;
    font-size: 13px;
  }
  
  .premium-icon {
    font-size: 60px;
  }
}
</style>